import React, { PureComponent } from 'react';
import styled from 'styled-components';
import SuccessIcon from '../assets/icons/success-icon.svg';

import Button from '../ui_components/button/button';
import { Modal, ModalActions, ModalContent, ModalHeader } from '../ui_components/modal';

const ModalContainer = styled(Modal)`
  width: 480px;
  height: 400px;
  bottom: 0;
`;

interface EstimateModalOwnProps {
  isModalOpen: boolean;
  onClose(): void;
  estimateId?: string;
  qbEstimateDocNumber?: string;
}

export interface EstimateModalProps extends EstimateModalOwnProps {
  accountId: number;
}

const BasicButton = styled(Button)`
  box-shadow: none;
  border: 1px solid #c6c6c6;
  border-radius: 8px !important;
  display: block;
`;
const ConfirmButton = styled(BasicButton)`
  border-radius: 5px;
  box-shadow: none;
  line-height: 0.6rem;
  &:hover {
    background: #c6c6c6;
  }
  min-width: 64px;
  margin-left: ${(props) => (props.className === 'pop-up' ? 'auto' : 'initial')};
`;

const EstimateContent = styled(ModalContent)`
  display: flex;
  flex-flow: row wrap;
  flex-direction: column;
  justify-content: space-between;
  padding: 25px;
`;

const SuccesMessage = styled.span`
  font-size: 18px;
  font-weight: 500;
`;

export const EstimateLink = styled.a`
  font-weight: 400;
  color: #2d778c;
  &:hover {
    color: #2d778c;
  }
`;

interface State {
  estimateId?: string;
  qbEstimateDocNumber?: string;
}

const getRestBaseEndpoint = () => {
  const options = (window as any).__DEPLOY_OPTIONS;
  return options.restApiUrl;
};
const qboUrl = () => {
  return !window.location.origin.includes('my.quipa')
    ? 'https://sandbox.qbo.intuit.com'
    : 'https://app.qbo.intuit.com';
};

// Session flags (client-only)
const QBO_SESSION_KEY = 'qboUserSession';
const QBO_LAST_TARGET_URL_KEY = 'qboLastTargetUrl';
const QBO_SESSION_TTL_MIN = 45;
const QBO_TARGET_WINDOW = 'qboTarget';
const QBO_OPENED_FLAG = 'qboOpenedAt';

// Guard against duplicate tab opens; reuse a named window
function openOnce(url: string | null | undefined) {
  if (!url) return;
  const last = Number(sessionStorage.getItem(QBO_OPENED_FLAG) || 0);
  if (Date.now() - last < 10000) return;
  sessionStorage.setItem(QBO_OPENED_FLAG, String(Date.now()));
  window.open(url, QBO_TARGET_WINDOW);
}

function buildPlainQboUrl(estimateId?: string) {
  if (!estimateId) return '';
  return `${qboUrl()}/app/estimate?txnId=${encodeURIComponent(estimateId)}`;
}

/**
 * Try to retarget a stored QBO URL to a new txnId.
 * Supports both:
 *  - https://.../app/estimate?txnId=123
 *  - https://.../login?deeplinkcompanyid=...&pagereq=app%2Festimate%3FtxnId%3D123
 * Returns the updated URL string, or null if not safely handled.
 */
function retargetQboUrlTxn(lastUrl: string, newTxnId: string): string | null {
  try {
    const u = new URL(lastUrl);

    // Only touch official QBO hosts
    if (!/\.qbo\.intuit\.com$/.test(u.hostname)) return null;

    // Case 1: direct app deeplink
    if (u.pathname.startsWith('/app/estimate')) {
      u.searchParams.set('txnId', newTxnId);
      return u.toString();
    }

    // Case 2: login deeplink with pagereq
    if (u.pathname.startsWith('/login') && u.searchParams.has('pagereq')) {
      const rawPageReq = u.searchParams.get('pagereq') || ''; // e.g. "app/estimate?txnId=663"
      const [path, qs = ''] = rawPageReq.split('?');
      if (!path.startsWith('app/estimate')) return null;

      const prParams = new URLSearchParams(qs);
      prParams.set('txnId', newTxnId);
      const newPageReq = `${path}?${prParams.toString()}`;
      u.searchParams.set('pagereq', newPageReq);
      return u.toString();
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Ensure sessionStorage has a QBO target URL for THIS estimate.
 * If a previous target exists and is compatible, retarget its txnId.
 * Otherwise, fall back to plain QBO deeplink for the estimate.
 */
function ensureLastTargetForEstimate(estimateId: string): string {
  const desiredPlain = buildPlainQboUrl(estimateId);
  const last = sessionStorage.getItem(QBO_LAST_TARGET_URL_KEY) || '';

  let next = last ? retargetQboUrlTxn(last, estimateId) : null;
  if (!next) next = desiredPlain;

  if (next !== last) {
    sessionStorage.setItem(QBO_LAST_TARGET_URL_KEY, next);
    // Allow a fresh open even if we opened very recently
    sessionStorage.removeItem(QBO_OPENED_FLAG);
  }
  return next;
}

export default class EstimateModal extends PureComponent<EstimateModalProps, State> {
  // Allow multiple trusted origins if needed (dev/staging/prod)
  private allowedOrigins = new Set<string>([
    'https://api.serverless-dev.quipa.com',
    'https://api.serverless-staging.quipa.com',
    'https://api.my.quipa.com',
  ]);

  constructor(props: EstimateModalProps) {
    super(props);
    this.state = {
      estimateId: props.estimateId,
      qbEstimateDocNumber: props.qbEstimateDocNumber,
    };
  }

  componentDidMount() {
    window.addEventListener('message', this.handleQboMessage);
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handleQboMessage);
  }

  private apiBase = () => getRestBaseEndpoint().replace(/\/$/, '');

  handleQboMessage = (event: MessageEvent) => {
    if (!this.allowedOrigins.has(event.origin)) return;
    const data = event.data;

    if (data === 'qboSessionActive' || (data && data.type === 'qboSessionActive')) {
      const expiry = Date.now() + QBO_SESSION_TTL_MIN * 60 * 1000;
      sessionStorage.setItem(QBO_SESSION_KEY, JSON.stringify({ active: true, expiresAt: expiry }));

      let targetUrl: string | undefined;
      if (data && data.qboUrl) {
        targetUrl = data.qboUrl; // often a login?deeplinkcompanyid=...&pagereq=...
      } else if (data && data.directUrlToken) {
        targetUrl = `${this.apiBase()}/quickBooks/direct?token=${encodeURIComponent(
          data.directUrlToken
        )}`;
      }

      if (targetUrl) {
        // If qbo host, retarget to current estimateId before storing
        const { estimateId } = this.state;
        if (estimateId) {
          const maybe = retargetQboUrlTxn(targetUrl, String(estimateId));
          sessionStorage.setItem(QBO_LAST_TARGET_URL_KEY, maybe || targetUrl);
        } else {
          sessionStorage.setItem(QBO_LAST_TARGET_URL_KEY, targetUrl);
        }
      } else if (this.state.estimateId) {
        const plain = buildPlainQboUrl(this.state.estimateId);
        sessionStorage.setItem(QBO_LAST_TARGET_URL_KEY, plain);
      }

      // Note: no window.open here — the poller will open it once.
    }
  };

  componentWillReceiveProps(nextProps: Readonly<EstimateModalProps>): void {
    if (this.props.estimateId !== nextProps.estimateId) {
      this.setState({ estimateId: nextProps.estimateId });
    }
    if (this.props.qbEstimateDocNumber !== nextProps.qbEstimateDocNumber) {
      this.setState({ qbEstimateDocNumber: nextProps.qbEstimateDocNumber });
    }
  }

  buildLaunchUrl = (opts: { silent: boolean; ui: 'iframe' | 'popup' }) => {
    const apiBase = this.apiBase();
    const { accountId } = this.props;
    const { estimateId } = this.state;

    const params = new URLSearchParams({
      accountId: String(accountId),
      type: 'estimate',
      txnId: String(estimateId || ''),
      ui: opts.ui,
    });
    if (opts.silent) params.set('silent', '1');
    return `${apiBase}/quickBooks/launch?${params.toString()}`;
  };

  openPopup = (url: string, width = 620, height = 700) => {
    const dualScreenLeft = (window as any).screenLeft || (window as any).screenX || 0;
    const dualScreenTop = (window as any).screenTop || (window as any).screenY || 0;
    const w = window.innerWidth || document.documentElement.clientWidth || screen.width;
    const h = window.innerHeight || document.documentElement.clientHeight || screen.height;
    const left = w / 2 - width / 2 + dualScreenLeft;
    const top = h / 2 - height / 2 + dualScreenTop;

    const features = `width=${Math.round(width)},height=${Math.round(height)},top=${Math.round(
      top
    )},left=${Math.round(
      left
    )},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=no`;

    return window.open(url, 'qboAuth', features);
  };

  private waitForPopupAndOpen = (popup: Window | null) => {
    const started = Date.now();
    const maxMs = QBO_SESSION_TTL_MIN * 60 * 1000;
    const iv = window.setInterval(() => {
      let active = false;
      try {
        const s = sessionStorage.getItem(QBO_SESSION_KEY);
        if (s) {
          const parsed = JSON.parse(s);
          active = !!(parsed && parsed.active && Date.now() < parsed.expiresAt);
        }
      } catch {
        active = false;
      }

      const closed = !popup || popup.closed;
      const timedOut = Date.now() - started > maxMs;

      if (active || closed || timedOut) {
        window.clearInterval(iv);

        // Extra safety: retarget *again* using current estimateId before opening
        const last = sessionStorage.getItem(QBO_LAST_TARGET_URL_KEY);
        const { estimateId } = this.state;

        if (last && estimateId) {
          const maybe = retargetQboUrlTxn(last, String(estimateId));
          if (maybe && maybe !== last) {
            sessionStorage.setItem(QBO_LAST_TARGET_URL_KEY, maybe);
          }
        }

        const target = sessionStorage.getItem(QBO_LAST_TARGET_URL_KEY);
        if (target) {
          openOnce(target);
          return;
        }

        if (estimateId) {
          const plain = buildPlainQboUrl(estimateId);
          openOnce(plain);
        }
      }
    }, 500);
  };

  private handleEstimateLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (this.state.estimateId) this.handleOpenInQbo();
  };

  private handleEstimateLinkKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
    if ((e.key === 'Enter' || e.key === ' ') && this.state.estimateId) {
      e.preventDefault();
      this.handleOpenInQbo();
    }
  };

  handleOpenInQbo = () => {
    const { estimateId } = this.state;
    if (!estimateId) return;

    // Always ensure last target points to THIS txnId (retarget if needed)
    ensureLastTargetForEstimate(String(estimateId));

    const sessionDataString = sessionStorage.getItem(QBO_SESSION_KEY);
    if (sessionDataString) {
      try {
        const sessionData = JSON.parse(sessionDataString);
        const hasActiveSession = sessionData.active && Date.now() < sessionData.expiresAt;

        if (hasActiveSession) {
          // Open the (retargeted) last target directly
          const lastTarget = sessionStorage.getItem(QBO_LAST_TARGET_URL_KEY);
          if (lastTarget) {
            openOnce(lastTarget);
            return;
          }
          // As a fallback, kick a silent launch to refresh session and target
          const silentUrl = this.buildLaunchUrl({ silent: true, ui: 'popup' });
          const popup = this.openPopup(silentUrl, 620, 700);
          this.waitForPopupAndOpen(popup);
          return;
        }
      } catch {
        sessionStorage.removeItem(QBO_SESSION_KEY);
      }
    }

    // No active session -> interactive/silent launch flow
    const silentUrl = this.buildLaunchUrl({ silent: true, ui: 'popup' });
    const popup = this.openPopup(silentUrl, 620, 700);
    this.waitForPopupAndOpen(popup);

    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      const interactiveUrl = this.buildLaunchUrl({ silent: false, ui: 'popup' });
      window.location.href = interactiveUrl; // fallback navigation (same tab)
    }
  };

  render() {
    const { isModalOpen, onClose } = this.props;
    const { estimateId, qbEstimateDocNumber } = this.state;

    return (
      <ModalContainer
        uiActive={isModalOpen}
        uiOnRequestClose={() => {}}
        uiDefaultSizes={false}
        data-qa="estimate-qbo-success-modal"
      >
        <ModalHeader style={{ display: 'flex', alignItems: 'center' }}>
          <SuccessIcon />
          <SuccesMessage>Success!</SuccesMessage>
        </ModalHeader>
        <EstimateContent>
          <div>
            Your item(s) were successfully sent to QuickBooks. QUIPA will not overwrite any client
            info (email, address, taxability) if the client is already found in QuickBooks. Here is
            your QuickBooks estimate:
          </div>

          <EstimateLink
            href={buildPlainQboUrl(estimateId)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={this.handleEstimateLinkClick}
            onKeyDown={this.handleEstimateLinkKeyDown}
            role="link"
            aria-disabled={!estimateId}
            data-qa="estimate-qbo-success-link"
            style={{
              textAlign: 'left',
              marginBottom: '30px',
              textDecoration: 'underline',
              cursor: estimateId ? 'pointer' : 'not-allowed',
              opacity: estimateId ? 1 : 0.6,
              pointerEvents: estimateId ? 'auto' : 'none',
            }}
          >
            {`Estimate: ${qbEstimateDocNumber || estimateId || ''}`}
          </EstimateLink>

          <div>
            <div style={{ fontWeight: 400 }}>We will update your QUIPA estimate status to</div>
            <div>
              <span style={{ color: '#A56909', fontWeight: 500 }}> “Exported to QuickBooks”</span>
            </div>
          </div>
        </EstimateContent>
        <ModalActions style={{ borderTop: 'none' }}>
          <ConfirmButton
            onClick={onClose}
            id="confirm"
            uiColor="#3B97B1"
            data-qa="estimate-qbo-success-close"
          >
            Close
          </ConfirmButton>
        </ModalActions>
      </ModalContainer>
    );
  }
}
